import React from 'react'
import { Stack, Typography } from '@mui/material'

import { Autocomplete, FieldLabel } from '@repo/ui'
// Direct file imports, NOT the `components` barrel — same reason `BrandContextCard`
// gives: the barrel re-exports this tree and reaching back through it is how the
// `ActionLine` / `ScanTargetButton` cycles were introduced.
import { CompanyAvatar } from 'components/display/CompanyAvatar/CompanyAvatar'

/** The company rows this picker lists. */
export interface PickableCompany {
  id: string
  name: string
  logo?: string | null
}

interface CompanyPickerProps {
  companies: PickableCompany[]
  value: PickableCompany | null
  onChange: (company: PickableCompany | null) => void
  /** Field label. "Preview as" on the notifications lab. */
  label?: string
  placeholder?: string
}

/**
 * ONE ROW OF THE LIST — the house company treatment, and the reason this file exists.
 *
 * `CompanyAvatar` rather than a hand-rolled `<Avatar>`: it is the single resolver for
 * logo -> initials -> store glyph, and it draws a company as a ROUNDED SQUARE on a white
 * plate (VERA-880 — the shape signals "organization", the plate keeps a dark-ink mark
 * legible in dark mode, and `objectFit: contain` shows the whole mark instead of cropping
 * it to a circle).
 *
 * ⚠️ `minWidth: 0` on BOTH the Stack and the Typography is load-bearing, not defensive.
 * `noWrap` alone does nothing inside a flex row: a flex item's `min-width` defaults to
 * `auto`, so the text refuses to shrink below its own content width, the row grows past
 * the panel, and an ancestor's `overflow: hidden` CLIPS it — no ellipsis, no affordance,
 * the name simply runs off the edge. That is exactly what the previous picker did:
 * measured at 1280x800, the paper's right edge was 1230px while the "VRG361 Takeover
 * Live" row's content box reached 1246px, and every name reported
 * `scrollWidth === clientWidth` (i.e. CSS thought nothing was overflowing). With these,
 * the ellipsis engages inside the row and nothing leaves the panel.
 */
const CompanyRow = ({ company }: { company: PickableCompany }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={3}
    sx={{ flex: 1, minWidth: 0 }}
  >
    <CompanyAvatar logo={company.logo} name={company.name} size={28} />
    <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
      {company.name}
    </Typography>
  </Stack>
)

/**
 * Pick the company a preview is rendered against.
 *
 * ## Why this is the shared `@repo/ui` `Autocomplete` and not a `Popover`
 *
 * §28/§47: the searchable single-select already exists at layer 1, and it brings the
 * whole house treatment with it — the `fieldChrome` trigger (49px, `primary[100]` rest
 * border, brand border + soft ring on focus), the leading search glyph, the chevron, the
 * 8px trigger->paper gap, the flat paper (`backgroundImage: 'none'`, which is the line
 * that kills MUI's dark-mode elevation overlay — §69), the divider edge, and 48px rows
 * with the brand-tint hover. A hand-rolled `Popover` reproduces none of that by default,
 * and the parts it silently omits are exactly the ones a reader sees.
 *
 * What it does NOT bring is the row's CONTENT, which is `renderOption`'s job and is the
 * one thing this file supplies.
 *
 * ## Why not `SearchFilter` / `SearchSelect`, which is what this replaced
 *
 * `SearchSelect` picks its row renderer from a STRING (`searchParams.preview ===
 * 'companies'` -> `CompanyPreview`), so a caller cannot change the row without changing
 * it for every surface. And `CompanyPreview` is not the company treatment: it draws a
 * CIRCULAR MUI `Avatar` (measured `border-radius: 50%`) with a `Building2` glyph and no
 * initials fallback, inside its own `1px solid grey[300]` box — so every option read as a
 * bordered card with a person-shaped avatar, and the picked brand's logo was cropped to a
 * circle rather than contained.
 *
 * Fixing `CompanyPreview` itself is the better end state (§36 already lists it as debt:
 * "CompanyPreview/OwnerPreview bypass CompanyAvatar/UserBlock") and is deliberately NOT
 * done here — it renders on the certificates, owners and add-modal pickers too, so it is
 * a change to those surfaces, not to this one.
 *
 * ## Clearing
 *
 * The shared `Autocomplete` replaces MUI's `endAdornment` wholesale with its own
 * chevron/spinner, so MUI's own clear indicator never renders. The Clear therefore rides
 * in `FieldLabel`'s `action` slot — the same slot, and the same wording, `FilterField`
 * uses for every filter in the drawer. It is `visibility: hidden` rather than unmounted
 * when there is nothing to clear, so picking a company does not shift the field below it.
 */
export const CompanyPicker = ({
  companies,
  value,
  onChange,
  label = 'Preview as',
  placeholder = 'Search companies',
}: CompanyPickerProps) => (
  <Stack>
    <FieldLabel
      label={label}
      action={
        <Typography
          variant="body3"
          onClick={value ? () => onChange(null) : undefined}
          sx={{
            textDecoration: 'underline',
            cursor: 'pointer',
            color: 'text.secondary',
            visibility: value ? 'visible' : 'hidden',
            '&:hover': { color: 'text.primary' },
          }}
        >
          Clear
        </Typography>
      }
    />
    <Autocomplete
      options={companies}
      value={value}
      // `null`, never `undefined` — handing MUI `undefined` flips the input from
      // controlled to uncontrolled mid-life and React warns about it.
      onChange={(next: PickableCompany | null) => onChange(next ?? null)}
      getOptionLabel={(company: PickableCompany) => company?.name ?? ''}
      isOptionEqualToValue={(a: PickableCompany, b: PickableCompany) => a?.id === b?.id}
      placeholder={placeholder}
      noOptionsText="No companies"
      renderOption={(company: PickableCompany) => <CompanyRow company={company} />}
    />
  </Stack>
)
